/**
 * The one place a Rapidry invoice is displayed in the dashboard.
 *
 * It embeds the document; it never draws one. Every invoice in the system is
 * produced by backend/packages/invoice (docs/INVOICE_DESIGN.md) so that admin
 * print, the customer's download, WhatsApp and email are the same bytes. A
 * React lookalike here would be a second renderer, which is exactly the drift
 * that `scripts/check-invoice-boundary.js` fails the build over - so this
 * component takes a `src` (an object URL or a signed URL) and shows it in a
 * frame, or shows an honest empty state when there is nothing to show.
 */
import type { ReactNode } from 'react'
import { FileText } from 'lucide-react'

import { useColors } from '../../../hooks/useColors'
import EmptyState from '../../../components/shared/EmptyState'
import { CARD_RADIUS } from './tokens'

interface InvoiceDocumentFrameProps {
  /** Object URL or signed URL of the rendered PDF. Absent while none exists. */
  src?: string | null
  /** Accessible name for the frame - screen readers announce this. */
  title: string
  emptyTitle: string
  emptyDescription: string
  /** Optional call to action shown under the empty state. */
  action?: ReactNode
  height?: number
}

export default function InvoiceDocumentFrame({
  src,
  title,
  emptyTitle,
  emptyDescription,
  action,
  height = 720,
}: InvoiceDocumentFrameProps) {
  const colors = useColors()

  return (
    <div
      data-testid="invoice-document-frame"
      style={{
        background: colors.bgSecondary,
        border: `1px solid ${colors.border}`,
        borderRadius: CARD_RADIUS,
        overflow: 'hidden',
        minHeight: `${height}px`,
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
      }}
    >
      {src ? (
        <iframe
          title={title}
          src={src}
          style={{ width: '100%', height: `${height}px`, border: 'none', display: 'block' }}
        />
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '4px' }}>
          <EmptyState icon={FileText} title={emptyTitle} description={emptyDescription} />
          {action}
        </div>
      )}
    </div>
  )
}
